import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { QuarantineStatus } from "../generated/graphql";

// Styled Components
const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  max-width: 400px;
`;

const Notification = styled.div<{
  type: "success" | "warning" | "error" | "info";
}>`
  background-color: ${(props) => {
    switch (props.type) {
      case "success":
        return "#d4edda";
      case "warning":
        return "#fff3cd";
      case "error":
        return "#f8d7da";
      case "info":
        return "#d1ecf1";
      default:
        return "#e2e3e5";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.type) {
        case "success":
          return "#c3e6cb";
        case "warning":
          return "#ffeaa7";
        case "error":
          return "#f5c6cb";
        case "info":
          return "#bee5eb";
        default:
          return "#d6d8db";
      }
    }};
  color: ${(props) => {
    switch (props.type) {
      case "success":
        return "#155724";
      case "warning":
        return "#856404";
      case "error":
        return "#721c24";
      case "info":
        return "#0c5460";
      default:
        return "#6c757d";
    }
  }};
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const NotificationMessage = styled.div`
  font-size: 14px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  margin-left: 12px;
  padding: 0;

  &:hover {
    opacity: 1;
  }
`;

const StatusIcon = styled.span<{ status: QuarantineStatus }>`
  margin-right: 8px;
  font-size: 16px;

  &::before {
    content: ${(props) => {
      switch (props.status) {
        case QuarantineStatus.Clean:
          return '"✅"';
        case QuarantineStatus.Quarantined:
          return '"⚠️"';
        case QuarantineStatus.ScanFailed:
          return '"❌"';
        default:
          return '"ℹ️"';
      }
    }};
  }
`;

// Notification types
export interface NotificationData {
  id: string;
  type: "success" | "warning" | "error" | "info";
  title: string;
  message: string;
  fileName?: string;
  quarantineStatus?: QuarantineStatus;
  autoClose?: boolean;
  duration?: number; // in milliseconds
}

interface QuarantineNotificationsProps {
  notifications: NotificationData[];
  onClose: (id: string) => void;
}

const QuarantineNotifications: React.FC<QuarantineNotificationsProps> = ({
  notifications,
  onClose,
}) => {
  const [visibleNotifications, setVisibleNotifications] =
    useState<NotificationData[]>(notifications);

  useEffect(() => {
    setVisibleNotifications(notifications);
  }, [notifications]);

  useEffect(() => {
    // Auto-close notifications that have autoClose enabled
    const timers: NodeJS.Timeout[] = [];

    notifications.forEach((notification) => {
      if (notification.autoClose) {
        const timer = setTimeout(() => {
          onClose(notification.id);
        }, notification.duration || 5000);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [notifications, onClose]);

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <NotificationContainer>
      {visibleNotifications.map((notification) => (
        <Notification key={notification.id} type={notification.type}>
          <NotificationContent>
            <NotificationTitle>
              {notification.quarantineStatus && (
                <StatusIcon status={notification.quarantineStatus} />
              )}
              {notification.title}
            </NotificationTitle>
            <NotificationMessage>
              {notification.message}
              {notification.fileName && (
                <div style={{ fontStyle: "italic", marginTop: "4px" }}>
                  File: {notification.fileName}
                </div>
              )}
            </NotificationMessage>
          </NotificationContent>
          <CloseButton onClick={() => onClose(notification.id)}>×</CloseButton>
        </Notification>
      ))}
    </NotificationContainer>
  );
};

export default QuarantineNotifications;

// Helper function to create quarantine-related notifications
export const createQuarantineNotification = (
  fileName: string,
  quarantineStatus: QuarantineStatus,
  reason?: string,
): NotificationData => {
  const id = `quarantine-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  switch (quarantineStatus) {
    case QuarantineStatus.Clean:
      return {
        id,
        type: "success",
        title: "File Scan Complete",
        message: "File has been scanned and marked as clean.",
        fileName,
        quarantineStatus,
        autoClose: true,
        duration: 5000,
      };

    case QuarantineStatus.Quarantined:
      return {
        id,
        type: "error",
        title: "File Quarantined",
        message: reason
          ? `File has been quarantined: ${reason}`
          : "File has been quarantined due to security concerns.",
        fileName,
        quarantineStatus,
        autoClose: false, // Don't auto-close quarantine notifications
      };

    case QuarantineStatus.ScanFailed:
      return {
        id,
        type: "warning",
        title: "Scan Failed",
        message:
          "Unable to complete security scan. File may need manual review.",
        fileName,
        quarantineStatus,
        autoClose: false,
      };

    default:
      return {
        id,
        type: "info",
        title: "File Status Update",
        message: "File security status has been updated.",
        fileName,
        quarantineStatus,
        autoClose: true,
        duration: 5000,
      };
  }
};

// Helper function to create scan progress notifications
export const createScanProgressNotification = (
  fileName: string,
  isScanning: boolean,
): NotificationData => {
  const id = `scan-progress-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  return {
    id,
    type: "info",
    title: isScanning ? "Scanning File" : "Scan Queued",
    message: isScanning
      ? "File is currently being scanned for malware..."
      : "File has been queued for security scanning.",
    fileName,
    autoClose: isScanning ? false : true,
    duration: 3000,
  };
};

// Helper function to create admin action notifications
export const createAdminActionNotification = (
  action: "restore" | "quarantine",
  fileName: string,
  success: boolean,
  message?: string,
): NotificationData => {
  const id = `admin-action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const actionLabels = {
    restore: "Restore",
    quarantine: "Quarantine",
  };

  return {
    id,
    type: success ? "success" : "error",
    title: `${actionLabels[action]} ${success ? "Successful" : "Failed"}`,
    message:
      message ||
      (success
        ? `File has been ${action}d successfully.`
        : `Failed to ${action} file. Please try again.`),
    fileName,
    autoClose: true,
    duration: success ? 4000 : 6000,
  };
};
